// src/pages/AITrainingData.js — Quản lý dữ liệu huấn luyện AI

import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardHeader, CardFooter, Label, Button, Input, Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import reportService from 'services/report.service';

const AITrainingData = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterTool, setFilterTool] = useState('');
    const [exporting, setExporting] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        fetchData();
    }, [page, filterTool]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const result = await reportService.getTrainingData(page, 20, filterTool);
            if (result.success) {
                setData(result.data);
                setTotalRecords(result.totalRecords);
                setTotalPages(result.totalPages);
            }
        } catch (err) {
            console.error('Lỗi lấy training data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Xóa mẫu này?',
            text: 'Mẫu huấn luyện sẽ bị xóa vĩnh viễn.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy',
            confirmButtonColor: '#e74c3c'
        });

        if (result.isConfirmed) {
            try {
                await reportService.deleteTrainingData(id);
                fetchData();
                Swal.fire({ icon: 'success', title: 'Đã xóa!', timer: 1500, showConfirmButton: false });
            } catch (err) {
                Swal.fire('Lỗi', 'Không thể xóa', 'error');
            }
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            await reportService.exportTrainingJSONL();
            Swal.fire({
                icon: 'success',
                title: 'Export thành công!',
                text: `${totalRecords} mẫu đã được export thành JSONL`,
                timer: 2500,
                showConfirmButton: false
            });
        } catch (err) {
            Swal.fire('Lỗi', 'Không thể export', 'error');
        } finally {
            setExporting(false);
        }
    };

    const getReadinessColor = () => {
        if (totalRecords >= 50) return '#27ae60';
        if (totalRecords >= 20) return '#e67e22';
        return '#e74c3c';
    };

    const getReadinessText = () => {
        if (totalRecords >= 50) return 'Sẵn sàng fine-tune';
        if (totalRecords >= 20) return 'Gần đủ dữ liệu';
        return 'Cần thêm dữ liệu';
    };

    return (
        <div className="content">
            <Row>
                <Col md="12" lg="10">
                    <Card style={{
                        borderRadius: '20px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: 'none',
                        overflow: 'hidden'
                    }}>
                        <CardHeader style={{
                            background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)',
                            padding: '20px 24px',
                            borderBottom: 'none'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <i className="fas fa-database" style={{ fontSize: '32px', color: '#fff', opacity: 0.9 }}></i>
                                    <div style={{ lineHeight: '1.3' }}>
                                        <Label style={{ fontSize: '18px', color: '#fff', fontWeight: '700', marginBottom: '0', display: 'block' }}>
                                            AI Training Data
                                        </Label>
                                        <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                                            Dữ liệu huấn luyện cho fine-tuning OpenAI
                                        </span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        padding: '6px 14px',
                                        borderRadius: '20px',
                                        backgroundColor: getReadinessColor(),
                                        color: '#fff',
                                        fontSize: '13px',
                                        fontWeight: '600'
                                    }}>
                                        📊 {totalRecords} mẫu — {getReadinessText()}
                                    </div>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody style={{ padding: '24px' }}>
                            {/* Toolbar */}
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                                <Input
                                    type="text"
                                    value={filterTool}
                                    onChange={(e) => { setFilterTool(e.target.value); setPage(1); }}
                                    placeholder="Lọc theo tool (VD: dhbrender)"
                                    style={{
                                        borderRadius: '10px',
                                        fontSize: '13px',
                                        border: '1.5px solid #e9ecef',
                                        maxWidth: '250px'
                                    }}
                                />
                                <Button
                                    color="success"
                                    disabled={exporting || totalRecords === 0}
                                    onClick={handleExport}
                                    style={{
                                        borderRadius: '10px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        padding: '8px 16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        marginLeft: 'auto'
                                    }}
                                >
                                    {exporting ? (
                                        <><Spinner size="sm" /> Đang export...</>
                                    ) : (
                                        <><i className="fas fa-download"></i> Export JSONL</>
                                    )}
                                </Button>
                            </div>

                            {/* Data List */}
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                    <Spinner style={{ marginBottom: '8px' }} />
                                    <div>Đang tải...</div>
                                </div>
                            ) : data.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                    <i className="fas fa-inbox" style={{ fontSize: '40px', marginBottom: '12px', display: 'block', opacity: 0.4 }}></i>
                                    <div>Chưa có dữ liệu huấn luyện</div>
                                    <small>Dữ liệu sẽ tự động được lưu khi admin phản hồi báo cáo lỗi</small>
                                </div>
                            ) : (
                                <div>
                                    {data.map((item) => (
                                        <div
                                            key={item.id}
                                            style={{
                                                padding: '14px 16px',
                                                borderRadius: '12px',
                                                backgroundColor: expandedId === item.id ? '#f0f4ff' : '#f8f9fa',
                                                marginBottom: '8px',
                                                border: `1px solid ${expandedId === item.id ? '#3498db' : '#e9ecef'}`,
                                                transition: 'all 0.2s',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <span style={{
                                                    padding: '3px 10px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#3498db',
                                                    color: '#fff',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    flexShrink: 0,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {item.toolName}
                                                </span>
                                                <div style={{
                                                    flex: 1,
                                                    fontSize: '13px',
                                                    color: '#2c3e50',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {item.userMessage?.substring(0, 100)}...
                                                </div>
                                                <small style={{ color: '#8898aa', flexShrink: 0, fontSize: '12px' }}>
                                                    {new Date(item.createdAt).toLocaleDateString('vi-VN')}
                                                </small>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: '#e74c3c',
                                                        fontSize: '14px',
                                                        padding: '4px 8px',
                                                        flexShrink: 0
                                                    }}
                                                    title="Xóa"
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            </div>

                                            {/* Expanded Details */}
                                            {expandedId === item.id && (
                                                <div style={{ marginTop: '12px', borderTop: '1px solid #e0e0e0', paddingTop: '12px' }}>
                                                    <div style={{ marginBottom: '10px' }}>
                                                        <Label style={{ fontSize: '11px', fontWeight: '700', color: '#3498db', textTransform: 'uppercase' }}>
                                                            📤 User Message (Input)
                                                        </Label>
                                                        <div style={{
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#fff',
                                                            fontSize: '12px',
                                                            fontFamily: 'monospace',
                                                            whiteSpace: 'pre-wrap',
                                                            border: '1px solid #e9ecef',
                                                            maxHeight: '150px',
                                                            overflow: 'auto'
                                                        }}>
                                                            {item.userMessage}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Label style={{ fontSize: '11px', fontWeight: '700', color: '#27ae60', textTransform: 'uppercase' }}>
                                                            🤖 Assistant Message (Output)
                                                        </Label>
                                                        <div style={{
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#fff',
                                                            fontSize: '12px',
                                                            whiteSpace: 'pre-wrap',
                                                            border: '1px solid #e9ecef',
                                                            maxHeight: '200px',
                                                            overflow: 'auto'
                                                        }}>
                                                            {item.assistantMessage}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardBody>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <CardFooter style={{
                                padding: '16px 24px',
                                borderTop: '1px solid #f0f0f0',
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '8px'
                            }}>
                                <Button
                                    size="sm"
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </Button>
                                <span style={{ padding: '6px 12px', fontSize: '13px', color: '#555' }}>
                                    Trang {page} / {totalPages}
                                </span>
                                <Button
                                    size="sm"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </Button>
                            </CardFooter>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AITrainingData;
